/**
 * Created by Cascade on 22.01.2026
 * Challenges Screen - Shows all challenges (active, upcoming, past)
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useLanguage } from '../../Hooks/useLanguage';
import { RouteChallenge, RouteChallengeApi } from '../../Model/Engagement/RouteChallenge/RouteChallenge';
import {
  IndividualChallenge,
  IndividualChallengeApi,
} from '../../Model/Engagement/IndividualChallenge/IndividualChallenge';
import { AthleteTemplateImpactSummary, MyImpactApi } from '../../Model/Engagement/Impact/MyImpact';
import { ChallengesTab } from './Components/ChallengesTab';
import { LoadingContainer } from '../../Components/LoadingContainer';
import { Theme } from '../../Theme/Theme';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ScreenContainer = styled.div`
  background: #f8f9fa;
  min-height: 100vh;
  animation: ${fadeIn} 0.3s ease-out;
`;

const PageHeader = styled.div`
  background: white;
  padding: 20px 0;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
  position: sticky;
  top: 60px;
  z-index: 10;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  background: ${Theme.Colors.lightestGrey};
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: ${Theme.Colors.lighterGrey};
  }
`;

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: ${Theme.Colors.textColor};
`;

const PageContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 16px 48px;
`;

export const MotivationChallengesScreen: React.FC = observer(() => {
  const { t } = useTrans();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [routeChallenges, setRouteChallenges] = useState<RouteChallenge[]>([]);
  const [individualChallenges, setIndividualChallenges] = useState<IndividualChallenge[]>([]);
  const [pastRouteChallenges, setPastRouteChallenges] = useState<RouteChallenge[]>([]);
  const [pastIndividualChallenges, setPastIndividualChallenges] = useState<IndividualChallenge[]>([]);
  const [impactSummaries, setImpactSummaries] = useState<AthleteTemplateImpactSummary[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [route, individual, myRouteChallenges, myIndividualChallenges, impacts] = await Promise.all([
          RouteChallengeApi.current().catch(() => []),
          IndividualChallengeApi.current().catch(() => []),
          RouteChallengeApi.myChallenges({ size: 50 }).catch(() => []),
          IndividualChallengeApi.myChallenges({ size: 50 }).catch(() => []),
          MyImpactApi.getTemplateSummaries(language).catch(() => []),
        ]);
        setRouteChallenges(route ?? []);
        setIndividualChallenges(individual ?? []);
        setPastRouteChallenges(myRouteChallenges ?? []);
        setPastIndividualChallenges(myIndividualChallenges ?? []);
        setImpactSummaries(impacts ?? []);
      } catch (err) {
        console.error('Error fetching challenges:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [language]);

  if (loading) {
    return <LoadingContainer text={t('motivation.loadingChallenges', { defaultValue: 'Loading challenges...' })} />;
  }

  return (
    <ScreenContainer>
      <PageHeader>
        <HeaderContent>
          <BackButton onClick={() => navigate(-1)}>←</BackButton>
          <HeaderTitle>🏆 {t('motivation.allChallenges', { defaultValue: 'All Challenges' })}</HeaderTitle>
        </HeaderContent>
      </PageHeader>

      <PageContent>
        <ChallengesTab
          routeChallenges={routeChallenges}
          individualChallenges={individualChallenges}
          pastRouteChallenges={pastRouteChallenges}
          pastIndividualChallenges={pastIndividualChallenges}
          impactSummaries={impactSummaries}
        />
      </PageContent>
    </ScreenContainer>
  );
});
